import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { getSession, startSession, submitSession, type AssessmentSessionDto, type SessionResponseItem } from '../../lib/assessmentSessions';
import { listAssessmentTemplates, type AssessmentTemplateDto } from '../../lib/assessmentTemplates';
import { ArrowLeft, ClipboardList, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/kinerja/assessments/$sessionId')({
  component: SessionDetailPage,
});

function SessionDetailPage() {
  return (
    <ProtectedRoute minimumRole="employee">
      <SessionDetailContent />
    </ProtectedRoute>
  );
}

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: AlertCircle, variant: 'warning' as const },
  in_progress: { label: 'In Progress', icon: Clock, variant: 'default' as const },
  completed: { label: 'Completed', icon: CheckCircle2, variant: 'success' as const },
  expired: { label: 'Expired', icon: AlertCircle, variant: 'secondary' as const },
};

const SCALE_LABELS: Record<number, string> = {
  1: 'Needs Improvement',
  2: 'Below Expectations',
  3: 'Meets Expectations',
  4: 'Exceeds Expectations',
  5: 'Outstanding',
};

type SectionResponse = {
  rating: number | null;
  comment: string;
};

function SessionDetailContent() {
  const { sessionId } = Route.useParams();
  const { user } = useAuth();
  const userId = user?.id ?? '';
  const companyId = user?.companyId ?? '';

  const [session, setSession] = useState<AssessmentSessionDto | null>(null);
  const [template, setTemplate] = useState<AssessmentTemplateDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Map of sectionId → { rating, comment }
  const [responses, setResponses] = useState<Record<string, SectionResponse>>({});

  useEffect(() => {
    let cancelled = false;
    getSession(sessionId).then(async (res) => {
      if (cancelled) return;
      if (res.success && res.data) {
        const sess = res.data.session;
        setSession(sess);
        // Load the template so we can render its sections
        const tmplRes = await listAssessmentTemplates(sess.companyId, true);
        if (!cancelled && tmplRes.success && tmplRes.data) {
          const tmpl = tmplRes.data.templates.find((t) => t.id === sess.templateId) ?? null;
          setTemplate(tmpl);
          // Pre-initialise response state for each section
          if (tmpl) {
            const init: Record<string, SectionResponse> = {};
            tmpl.sections.forEach((s) => { init[s.id] = { rating: null, comment: '' }; });
            setResponses(init);
          }
        }
      } else {
        setError(res.error ?? 'Session not found');
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [sessionId]);

  async function handleStart() {
    if (!session) return;
    setStarting(true);
    const res = await startSession(session.id, userId);
    setStarting(false);
    if (res.success && res.data) {
      toast.success('Assessment started');
      setSession(res.data.session);
    } else {
      toast.error('Failed to start assessment');
    }
  }

  async function handleSubmit() {
    if (!session || !template) return;
    setSubmitError('');

    // Validate all sections have a rating
    const unanswered = template.sections.filter((s) => responses[s.id]?.rating === null);
    if (unanswered.length > 0) {
      setSubmitError(`Please rate all sections before submitting. Missing: ${unanswered.map((s) => s.title).join(', ')}`);
      return;
    }

    setSubmitting(true);

    const responseList: SessionResponseItem[] = template.sections.map((s) => ({
      sectionId: s.id,
      questionKey: 'rating',
      responseValue: responses[s.id]?.rating,
      score: responses[s.id]?.rating ?? 0,
    }));

    // Append comment responses
    template.sections.forEach((s) => {
      const comment = responses[s.id]?.comment?.trim();
      if (comment) {
        responseList.push({
          sectionId: s.id,
          questionKey: 'comment',
          responseValue: comment,
        });
      }
    });

    const res = await submitSession(session.id, { userId, companyId, responses: responseList });
    setSubmitting(false);
    if (res.success && res.data) {
      toast.success('Assessment submitted successfully');
      setSession(res.data.session);
      setSubmitted(true);
    } else {
      const msg = res.error ?? 'Failed to submit. Please try again.';
      toast.error(msg);
      setSubmitError(msg);
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 bg-muted rounded animate-pulse" />
        <div className="h-48 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="space-y-4">
        <Link to="/kinerja/my-assessments/" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" />
          Back to assessments
        </Link>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-destructive">{error || 'Session not found'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const cfg = STATUS_CONFIG[session.status];
  const StatusIcon = cfg.icon;
  const sections = template?.sections.slice().sort((a, b) => a.orderIndex - b.orderIndex) ?? [];

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Back nav */}
      <Link
        to="/kinerja/my-assessments/"
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to My Assessments
      </Link>

      {/* Session card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-accent shrink-0">
                <ClipboardList className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">{template?.name ?? 'Performance Assessment'}</CardTitle>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {session.dueDate
                    ? `Due ${new Date(session.dueDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                    : 'No due date'}
                </p>
              </div>
            </div>
            <Badge variant={cfg.variant} className="flex items-center gap-1 shrink-0">
              <StatusIcon className="w-3 h-3" />
              {cfg.label}
            </Badge>
          </div>
        </CardHeader>

        <Separator />

        <CardContent className="pt-4 space-y-4">
          {/* Metadata */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <p className="font-medium capitalize mt-0.5">{session.status.replace('_', ' ')}</p>
            </div>
            {session.startedAt && (
              <div>
                <p className="text-xs text-muted-foreground">Started</p>
                <p className="font-medium mt-0.5">
                  {new Date(session.startedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </p>
              </div>
            )}
            {session.completedAt && (
              <div>
                <p className="text-xs text-muted-foreground">Completed</p>
                <p className="font-medium mt-0.5">
                  {new Date(session.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </p>
              </div>
            )}
          </div>

          <Separator />

          {/* Actions */}
          {submitted ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle2 className="w-10 h-10 text-primary" />
              <p className="text-sm font-medium text-foreground">Assessment submitted successfully</p>
              <p className="text-xs text-muted-foreground">
                Your competency scores have been updated based on your responses.
              </p>
              <Button variant="outline" size="sm" asChild>
                <Link to="/kinerja/my-assessments/">Back to assessments</Link>
              </Button>
            </div>
          ) : session.status === 'pending' ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                This assessment has been assigned to you. Click below to begin.
              </p>
              <Button onClick={handleStart} disabled={starting}>
                {starting ? 'Starting…' : 'Start Assessment'}
              </Button>
            </div>
          ) : session.status === 'in_progress' ? (
            <div className="space-y-6">
              <p className="text-sm text-muted-foreground">
                Rate yourself on each competency area below, then submit.
              </p>

              {sections.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">
                  No sections defined for this template yet.
                </p>
              ) : (
                sections.map((section) => {
                  const resp = responses[section.id] ?? { rating: null, comment: '' };
                  return (
                    <div key={section.id} className="space-y-3 p-4 rounded-lg border bg-muted/30">
                      <p className="text-sm font-semibold text-foreground">{section.title}</p>

                      {/* Rating scale 1–5 */}
                      <div className="space-y-1.5">
                        <p className="text-xs text-muted-foreground">Rating</p>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map((v) => (
                            <button
                              key={v}
                              type="button"
                              title={SCALE_LABELS[v]}
                              onClick={() =>
                                setResponses((prev) => ({
                                  ...prev,
                                  [section.id]: { ...prev[section.id], rating: v },
                                }))
                              }
                              className={cn(
                                'flex-1 py-2 rounded-lg text-sm font-semibold border-2 transition-all',
                                resp.rating === v
                                  ? 'border-primary bg-primary text-primary-foreground'
                                  : 'border-input text-muted-foreground hover:border-primary/50 hover:text-foreground',
                              )}
                            >
                              {v}
                            </button>
                          ))}
                        </div>
                        {resp.rating !== null && (
                          <p className="text-xs text-primary">{SCALE_LABELS[resp.rating]}</p>
                        )}
                      </div>

                      {/* Optional comment */}
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Comments (optional)</p>
                        <Textarea
                          rows={2}
                          placeholder="Add any supporting comments…"
                          className="resize-none text-sm"
                          value={resp.comment}
                          onChange={(e) =>
                            setResponses((prev) => ({
                              ...prev,
                              [section.id]: { ...prev[section.id], comment: e.target.value },
                            }))
                          }
                        />
                      </div>
                    </div>
                  );
                })
              )}

              {submitError && (
                <div className="flex items-start gap-2 text-destructive text-xs bg-destructive/10 rounded-lg px-3 py-2">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  {submitError}
                </div>
              )}

              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Assessment'}
              </Button>
            </div>
          ) : session.status === 'completed' ? (
            <div className="flex items-center gap-2 text-sm text-primary">
              <CheckCircle2 className="w-4 h-4" />
              This assessment has been completed.
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertCircle className="w-4 h-4" />
              This assessment has expired.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
